"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="47025" className="hidden lg:flex items-center gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-secondary/50 animate-pulse rounded-full" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu className="hidden lg:flex">
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={cn(navigationMenuTriggerStyle(), "bg-transparent hover:bg-transparent focus:bg-transparent", "text-[12px] font-mono uppercase tracking-widest text-foreground/70 hover:text-primary transition-all hover:underline underline-offset-8 decoration-accent/40")}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-32 bg-secondary/50 animate-pulse rounded-md" data-index={i} />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6 mt-8">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col">
          <SheetClose asChild>
            <Link to={item.href || "#"} className="text-2xl font-serif font-medium text-foreground hover:text-accent transition-colors">
              {item.label}
            </Link>
          </SheetClose>
        </div>)}
      <div className="pt-6 mt-2 border-t border-secondary/30">
        <SheetClose asChild>
          <Button asChild className="w-full bg-accent text-accent-foreground hover:bg-accent/90 rounded-full font-sans" variant="default">
            <Link to="/ConnectPage">New Clients</Link>
          </Button>
        </SheetClose>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-500 border-b", isScrolled ? "bg-background/80 backdrop-blur-md border-secondary/40 shadow-sm" : "bg-background border-transparent")}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0 mr-4 lg:mr-8">
          <Link to="/HomePage" className="block">
            <img src="/img/ChatGPT-Image-Mar-20-2026-09_31_23-AM-copy.png" alt="Ostara Counseling" className="h-10 w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden lg:flex flex-1 justify-center">
          <MenuProvider menu_id="16">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden lg:flex items-center justify-end flex-shrink-0 ml-4">
          <Button asChild variant="default" className="bg-accent text-accent-foreground hover:bg-accent/90 transition-all rounded-full px-8 font-sans shadow-md hover:shadow-lg">
            <Link to="https://forms.gle/QyE7n12agc8JiUNj7" newTab aria-label="Open external intake form">Get Started</Link>
          </Button>
        </div>

        {}
        <div className="lg:hidden flex items-center">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="hover:bg-secondary/50 text-primary">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px] border-l border-secondary/30 bg-background">
              <SheetHeader className="text-left border-b border-secondary/30 pb-6">
                <SheetTitle>
                  <img src="/img/ChatGPT-Image-Mar-20-2026-09_31_23-AM-copy.png" alt="Ostara Counseling" className="h-8 !w-[95px]" />
                </SheetTitle>
              </SheetHeader>
              <MenuProvider menu_id="16">
                <MobileMenuConsumer />
              </MenuProvider>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>;
}